import { useCallback, useEffect, useState, useRef } from 'react';
import { STORAGE_KEYS } from '../../../constants/main';

const useContainerResize = (ref, storageKey, initialValue, dragLeft = false, minValue, maxValue) => {
  const [isResizing, setIsResizing] = useState(false);
  const [width, setWidth] = useState(
    JSON.parse(window.localStorage.getItem(STORAGE_KEYS.LIVECHAT_LAYOUT_PREFERENCES))?.[storageKey] ?? initialValue
  );

  const frameRequestRef = useRef(null);

  /* min/max values can be numbers or percentages */
  const calculateValue = (value) => {
    if (typeof value === 'string' && value.endsWith('%')) {
      const percentage = parseFloat(value.replace('%', ''));
      return (percentage / 100) * window.innerWidth;
    }
    return value;
  };

  const startResizing = useCallback((e) => {
    e.preventDefault();
    document.body.style.cursor = 'col-resize';
    setIsResizing(true);
  }, [width]);

  const stopResizing = useCallback(() => {
    document.body.style.cursor = 'initial';
    setIsResizing(false);
    if (frameRequestRef.current) {
      cancelAnimationFrame(frameRequestRef.current);
      frameRequestRef.current = null;
    }
  }, []);

  const resize = useCallback(
    (mouseMoveEvent) => {
      let newWidth;
      if (isResizing) {
        if (ref?.current) {
          newWidth = dragLeft
            ? ref.current.getBoundingClientRect().right - mouseMoveEvent.clientX
            : mouseMoveEvent.clientX - ref.current.getBoundingClientRect().left;
        } else {
          newWidth = dragLeft
            ? document.body.offsetWidth - (mouseMoveEvent.clientX - document.body.offsetLeft)
            : mouseMoveEvent.clientX - document.body.offsetLeft;
        }

        // Check for min and max constraints
        const lowest = calculateValue(minValue);
        const highest = calculateValue(maxValue);
        if (lowest !== undefined && newWidth < lowest) {
          newWidth = lowest;
        } else if (highest !== undefined && newWidth > highest) {
          newWidth = highest;
        }

        if (frameRequestRef.current) {
          cancelAnimationFrame(frameRequestRef.current);
        }
        frameRequestRef.current = requestAnimationFrame(() => setWidth(newWidth));
      }
    },
    [isResizing]
  );

  useEffect(() => {
    const timeout = setTimeout(() => {
      if (storageKey) {
        try {
          const layoutConfig = JSON.parse(window.localStorage.getItem(STORAGE_KEYS.LIVECHAT_LAYOUT_PREFERENCES));
          layoutConfig[storageKey] = width;
          window.localStorage.setItem(STORAGE_KEYS.LIVECHAT_LAYOUT_PREFERENCES, JSON.stringify(layoutConfig));
        } catch {
          return;
        }
      }
    }, 300);

    return () => clearTimeout(timeout);
  }, [width]);

  useEffect(() => {
    window.addEventListener('mousemove', resize);
    window.addEventListener('mouseup', stopResizing);
    return () => {
      window.removeEventListener('mousemove', resize);
      window.removeEventListener('mouseup', stopResizing);
    };
  }, [resize, stopResizing]);

  return { width, setWidth, isResizing, startResizing };
};

export default useContainerResize;
